import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useForm, usePage, router } from "@inertiajs/react";
import AppLayout from "@/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, MapPin, Users, Edit2, XCircle, Check, Clock, X, Navigation, Gamepad2, CalendarPlus } from "lucide-react";
import BoardGameList from "@/components/app/BoardGameList";
import MyLibrarySection from "@/components/app/MyLibrarySection";


export default function EventShow() {
  const { event, is_host, is_attendee, attendees, auth } = usePage().props;
  const startsAt = event.starts_at ? new Date(event.starts_at) : null;
  const endsAt   = event.ends_at   ? new Date(event.ends_at)   : null;
  const lat      = event.latitude  ? parseFloat(event.latitude)  : null;
  const lng      = event.longitude ? parseFloat(event.longitude) : null;

  const isCancelled = event.status === "cancelled";

  function cancelEvent() {
    if (!confirm("Cancel this event? Attendees will be notified.")) return;
    router.delete(event.paths.destroy);
  }

  return (
    <AppLayout>
      <div className="container py-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              {isCancelled && <Badge variant="destructive">Cancelled</Badge>}
              {!event.is_public && <Badge variant="secondary">Private</Badge>}
            </div>
            <h1 className="text-3xl font-bold">{event.title}</h1>
            <p className="text-muted-foreground mt-1">
              Hosted by{" "}
              <Link href={event.host.path} className="hover:text-foreground transition-colors">
                {event.host.full_name}
              </Link>
            </p>
          </div>

          {is_host && !isCancelled && (
            <div className="flex gap-2 shrink-0">
              <Button asChild variant="outline" size="sm">
                <Link href={event.paths.edit}>
                  <Edit2 className="h-4 w-4" />
                  Edit
                </Link>
              </Button>
              <Button variant="destructive" size="sm" onClick={cancelEvent}>
                <XCircle className="h-4 w-4" />
                Cancel event
              </Button>
            </div>
          )}
        </div>

        <div className="grid gap-6 md:grid-cols-[1fr_280px]">
          {/* Main column */}
          <div className="space-y-6">
            {/* Details + Map side by side on large screens */}
            <div className="grid gap-4 lg:grid-cols-2">
              {/* Details */}
              <div className="rounded-lg border border-border bg-card p-5 space-y-3">
                {startsAt && (
                  <DetailRow icon={<CalendarDays className="h-4 w-4" />}>
                    <div>
                      <p className="font-medium">
                        {startsAt.toLocaleDateString(undefined, {
                          weekday: "long", year: "numeric", month: "long", day: "numeric"
                        })}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {startsAt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                        {endsAt && ` – ${endsAt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`}
                      </p>
                    </div>
                  </DetailRow>
                )}

                {(event.location_name || event.address) && (
                  <DetailRow icon={<MapPin className="h-4 w-4" />}>
                    <p className="font-medium">{event.location_name || event.address}</p>
                  </DetailRow>
                )}

                {event.player_limit && (
                  <DetailRow icon={<Users className="h-4 w-4" />}>
                    <p>
                      Up to <strong>{event.player_limit}</strong> players
                      {!event.accepting_rsvps && !isCancelled && (
                        <span className="ml-2 text-destructive text-sm font-medium">· Full</span>
                      )}
                      {!event.accepting_rsvps && event.waitlist_count > 0 && (
                        <span className="ml-1 text-xs text-muted-foreground">· {event.waitlist_count} on waitlist</span>
                      )}
                    </p>
                  </DetailRow>
                )}
              </div>

              {/* Map */}
              {lat && lng ? (
                <div className="rounded-lg border border-border overflow-hidden flex flex-col">
                  <iframe
                    title="Event location"
                    width="100%"
                    className="flex-1 min-h-[280px]"
                    style={{ border: 0, display: "block" }}
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.0015},${lat - 0.001},${lng + 0.0015},${lat + 0.001}&layer=mapnik&marker=${lat},${lng}`}
                  />
                  <div className="px-4 py-2.5 bg-card border-t border-border shrink-0">
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                    >
                      <Navigation className="h-3.5 w-3.5" />
                      Get Directions
                    </a>
                  </div>
                </div>
              ) : <div />}
            </div>

            {/* Description */}
            {event.description && (
              <div className="rounded-lg border border-border bg-card p-5">
                <h2 className="font-semibold mb-3">About this event</h2>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                  {event.description}
                </p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* RSVP */}
            {auth.authenticated && !isCancelled && (
              <RsvpSection event={event} isAttendee={is_attendee} isHost={is_host} attendees={attendees} />
            )}
            {!auth.authenticated && !isCancelled && (
              <div className="rounded-lg border border-border bg-card p-4 text-center space-y-3">
                <p className="text-sm text-muted-foreground">Sign in to RSVP</p>
                <Button asChild className="w-full" size="sm">
                  <Link href={usePage().props.routes.login}>Sign in</Link>
                </Button>
              </div>
            )}

            {/* Attendees */}
            <AttendeeList attendees={attendees} />

            {/* Add to Calendar */}
            {!isCancelled && (
              <a
                href={event.paths.calendar}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <CalendarPlus className="h-4 w-4" />
                Add to Calendar
              </a>
            )}
          </div>
        </div>

      </div>

      {auth.authenticated && (
        <div className="px-4 sm:px-6 pb-8">
          <EventGamesSection routes={usePage().props.routes} />
        </div>
      )}
    </AppLayout>
  );
}

// ─── Games section ────────────────────────────────────────────

function EventGamesSection({ routes }) {
  const { games_data, event, is_host, is_attendee } = usePage().props;
  const [activeTab, setActiveTab] = useState("being_brought");

  // All Games infinite scroll state
  const [allGames, setAllGames] = useState([]);
  const [allGamesHasMore, setAllGamesHasMore] = useState(true);
  const [allGamesLoading, setAllGamesLoading] = useState(false);
  const nextCursorRef = useRef(null);
  const sentinelRef = useRef(null);
  const loadedOnceRef = useRef(false);

  if (!games_data) return null;

  const {
    being_brought, my_games,
    my_owned_bgg_ids, my_bring_map, my_wtp_map, my_can_teach_bgg_ids = [], games_paths,
  } = games_data;

  const myOwnedIds    = new Set(my_owned_bgg_ids);
  const bringIds      = new Set(Object.keys(my_bring_map).map(Number));
  const wtpIds        = new Set(Object.keys(my_wtp_map).map(Number));
  const myCanTeachIds = new Set(my_can_teach_bgg_ids);
  const canTeachIds   = new Set(being_brought.filter((g) => g.has_teacher).map((g) => g.bgg_id));

  const visitOpts = { preserveScroll: true, preserveState: true };

  function toggleBring(bggId) {
    if (bringIds.has(bggId)) {
      router.delete(`${games_paths.want_to_brings}/${my_bring_map[bggId]}`, visitOpts);
    } else {
      router.post(games_paths.want_to_brings, { bgg_game_id: bggId }, visitOpts);
    }
  }

  function toggleWtp(bggId) {
    if (wtpIds.has(bggId)) {
      router.delete(`${games_paths.want_to_plays}/${my_wtp_map[bggId]}`, visitOpts);
    } else {
      router.post(games_paths.want_to_plays, { bgg_game_id: bggId }, visitOpts);
    }
  }

  function toggleCanTeach(bggId) {
    if (myCanTeachIds.has(bggId)) {
      router.delete(`${routes.can_teach_library}/${bggId}/can_teach`, visitOpts);
    } else {
      router.post(`${routes.can_teach_library}/${bggId}/can_teach`, {}, visitOpts);
    }
  }

  // Fetch a page of All Games
  const fetchAllGames = useCallback(async () => {
    if (allGamesLoading) return;
    setAllGamesLoading(true);
    try {
      const url = new URL(games_paths.all_games, window.location.origin);
      if (nextCursorRef.current) url.searchParams.set("cursor", nextCursorRef.current);
      const res = await fetch(url.toString(), {
        headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin",
      });
      if (!res.ok) return;
      const json = await res.json();
      setAllGames((prev) => {
        const existingIds = new Set(prev.map((g) => g.bgg_id));
        return [...prev, ...json.data.filter((g) => !existingIds.has(g.bgg_id))];
      });
      nextCursorRef.current = json.meta.next_cursor;
      setAllGamesHasMore(json.meta.has_more);
    } finally {
      setAllGamesLoading(false);
    }
  }, [games_paths.all_games, allGamesLoading]);

  // Load first page when tab becomes active
  useEffect(() => {
    if (activeTab !== "all" || loadedOnceRef.current) return;
    loadedOnceRef.current = true;
    fetchAllGames();
  }, [activeTab]);

  // IntersectionObserver for sentinel
  useEffect(() => {
    if (activeTab !== "all" || !sentinelRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && allGamesHasMore && !allGamesLoading) {
          fetchAllGames();
        }
      },
      { rootMargin: "200px" }
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [activeTab, allGamesHasMore, allGamesLoading, fetchAllGames]);

  const tabs = [
    { value: "being_brought", label: "Being Brought" },
    { value: "all",           label: "All Games" },
    ...(my_games.length > 0 ? [ { value: "mine", label: "My Collection" } ] : []),
  ];

  return (
    <div className="mt-8 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gamepad2 className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Games</h2>
        </div>
        {(is_host || is_attendee) && event?.paths?.plays && (
          <Link
            href={event.paths.plays}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            <Gamepad2 className="h-3.5 w-3.5" />
            Log a Play
          </Link>
        )}
      </div>

      <TabBar tabs={tabs} active={activeTab} onChange={setActiveTab} />

      <div role="tabpanel" id={`tabpanel-${activeTab}`} aria-labelledby={`tab-${activeTab}`}>
        {activeTab === "mine" ? (
          <MyLibrarySection
            games={my_games}
            heading={null}
            mode="mine"
            myOwnedIds={myOwnedIds}
            bringIds={bringIds}
            wantToPlayIds={wtpIds}
            canTeachIds={canTeachIds}
            myCanTeachIds={myCanTeachIds}
            onBring={toggleBring}
            onWantToPlay={toggleWtp}
            onCanTeach={toggleCanTeach}
          />
        ) : activeTab === "all" ? (
          <>
            {allGames.length === 0 && allGamesLoading ? (
              <p className="text-sm text-muted-foreground py-4 text-center">Loading games…</p>
            ) : allGames.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No games yet.</p>
            ) : (
              <BoardGameList
                games={allGames}
                mode="attendee"
                myOwnedIds={myOwnedIds}
                bringIds={bringIds}
                wantToPlayIds={wtpIds}
                canTeachIds={canTeachIds}
                onBring={toggleBring}
                onWantToPlay={toggleWtp}
              />
            )}
            {/* Sentinel for IntersectionObserver */}
            <div ref={sentinelRef} className="h-1" />
            {allGamesLoading && allGames.length > 0 && (
              <p className="text-xs text-muted-foreground text-center py-3">Loading more…</p>
            )}
          </>
        ) : (
          <BoardGameList
            games={being_brought}
            mode="attendee"
            myOwnedIds={myOwnedIds}
            bringIds={bringIds}
            wantToPlayIds={wtpIds}
            canTeachIds={canTeachIds}
            onBring={toggleBring}
            onWantToPlay={toggleWtp}
          />
        )}
      </div>
    </div>
  );
}

function TabBar({ tabs, active, onChange }) {
  return (
    <div role="tablist" className="flex border-b border-border">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          role="tab"
          aria-selected={active === tab.value}
          aria-controls={`tabpanel-${tab.value}`}
          id={`tab-${tab.value}`}
          onClick={() => onChange(tab.value)}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors
            ${active === tab.value
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"}`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ─── RSVP ─────────────────────────────────────────────────────

function RsvpSection({ event, isAttendee, isHost, attendees }) {
  const auth = usePage().props.auth;
  const myAttendee = attendees.find((a) => a.user.id === auth.user?.id) ?? null;

  function rsvp(status) {
    router.post(event.paths.attendees, { status });
  }

  function updateRsvp(status) {
    if (!myAttendee) return;
    router.put(`${event.paths.attendees}/${myAttendee.id}`, { status });
  }

  function cancelRsvp() {
    if (!myAttendee) return;
    router.delete(`${event.paths.attendees}/${myAttendee.id}`);
  }

  const hostBadge = isHost && (
    <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
      Organizing this event
    </p>
  );

  // Waitlisted state
  if (myAttendee?.status === "waitlisted") {
    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-4 space-y-3">
        {hostBadge}
        <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
          You&apos;re on the waitlist
          {myAttendee.waitlist_position != null && ` (#${myAttendee.waitlist_position})`}
        </p>
        <div className="flex gap-2 flex-wrap">
          {event.accepting_rsvps && (
            <Button size="sm" onClick={() => updateRsvp("attending")}>
              <Check className="h-3.5 w-3.5" />
              Attend now
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={cancelRsvp}>
            <X className="h-3.5 w-3.5" />
            Leave waitlist
          </Button>
        </div>
      </div>
    );
  }

  if (myAttendee) {
    return (
      <div className="rounded-lg border border-border bg-card p-4 space-y-3">
        {hostBadge}
        <p className="text-sm font-medium">Your RSVP</p>
        <div className="flex gap-1.5 flex-wrap">
          <StatusButton
            icon={<Check className="h-3.5 w-3.5" />}
            label="Attending"
            active={myAttendee.status === "attending"}
            onClick={() => updateRsvp("attending")}
          />
          <StatusButton
            icon={<Clock className="h-3.5 w-3.5" />}
            label="Maybe"
            active={myAttendee.status === "maybe"}
            onClick={() => updateRsvp("maybe")}
          />
          <StatusButton
            icon={<X className="h-3.5 w-3.5" />}
            label="Not going"
            active={myAttendee.status === "declined"}
            onClick={() => cancelRsvp()}
            variant="ghost"
          />
        </div>
      </div>
    );
  }

  if (!event.accepting_rsvps && event.status === "published") {
    return (
      <div className="rounded-lg border border-border bg-card p-4 space-y-2">
        {hostBadge}
        <p className="text-sm text-muted-foreground text-center">This event is full</p>
        <Button size="sm" className="w-full" onClick={() => rsvp("waitlisted")}>
          Join Waitlist
        </Button>
      </div>
    );
  }

  if (!event.accepting_rsvps) {
    return (
      <div className="rounded-lg border border-border bg-card p-4 space-y-2">
        {hostBadge}
        <p className="text-sm text-muted-foreground text-center">This event is full</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-2">
      {hostBadge}
      <p className="text-sm font-medium">RSVP to this event</p>
      <div className="grid grid-cols-2 gap-2">
        <Button size="sm" onClick={() => rsvp("attending")}>
          <Check className="h-3.5 w-3.5" />
          Attending
        </Button>
        <Button size="sm" variant="outline" onClick={() => rsvp("maybe")}>
          <Clock className="h-3.5 w-3.5" />
          Maybe
        </Button>
      </div>
    </div>
  );
}

function StatusButton({ icon, label, active, onClick, variant }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors
        ${active
          ? "bg-primary text-primary-foreground"
          : variant === "ghost"
            ? "text-muted-foreground hover:text-foreground hover:bg-muted"
            : "border border-border bg-background hover:bg-muted"
        }`}
    >
      {icon}
      {label}
    </button>
  );
}

function AttendeeList({ attendees }) {
  if (attendees.length === 0) return null;

  const attending  = attendees.filter((a) => a.status === "attending");
  const maybe      = attendees.filter((a) => a.status === "maybe");
  const waitlisted = attendees.filter((a) => a.status === "waitlisted");

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-3">
      <h3 className="text-sm font-medium">
        Attendees{" "}
        <span className="text-muted-foreground font-normal">({attending.length})</span>
      </h3>

      {attending.length > 0 && (
        <div className="space-y-2">
          {attending.map((a) => (
            <AttendeeRow key={a.id} attendee={a} />
          ))}
        </div>
      )}

      {maybe.length > 0 && (
        <>
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Maybe ({maybe.length})</p>
          <div className="space-y-2">
            {maybe.map((a) => (
              <AttendeeRow key={a.id} attendee={a} dimmed />
            ))}
          </div>
        </>
      )}

      {waitlisted.length > 0 && (
        <>
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Waitlist ({waitlisted.length})</p>
          <div className="space-y-2">
            {waitlisted.map((a) => (
              <AttendeeRow key={a.id} attendee={a} dimmed />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function AttendeeRow({ attendee, dimmed }) {
  const { user } = attendee;
  const initials = user.full_name
    ? user.full_name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "?";

  return (
    <Link href={user.path} className={`flex items-center gap-2 hover:opacity-80 transition-opacity ${dimmed ? "opacity-60" : ""}`}>
      {user.avatar_url ? (
        <img src={user.avatar_url} alt={user.full_name} className="h-7 w-7 rounded-full object-cover" />
      ) : (
        <div className="h-7 w-7 rounded-full bg-accent text-accent-foreground text-[11px] font-medium flex items-center justify-center">
          {initials}
        </div>
      )}
      <span className="text-sm">{user.full_name}</span>
    </Link>
  );
}

function DetailRow({ icon, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="text-muted-foreground mt-0.5 shrink-0">{icon}</div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}
